import { EventEmitter, Injectable } from '@angular/core';
import { Filter, Order, OrderType, Paginator, Search, UtilService } from '@docpath/linces-angular-components';
import { Table, TableField, TableFieldBadge, TableFieldBadges, TableFieldDate, TableFieldIcon, TableFieldIcons, TableFieldIconsText, TableFieldText, TableRow } from '@docpath/webcomponents';
import { faComment, faEnvelope } from '@fortawesome/free-regular-svg-icons';

import { Priority } from '../../../configuration/alert-rules/models/alert-rule';
import { Alert, AlertVia } from '../../models/alert';
import { MonitoringConstants } from '../../monitoring-constants';
import { MonitoringHttpService } from '../../services/monitoring-http.service';

@Injectable({
    providedIn: 'root'
})
export class MonitoringTableService {

    alertsLoaded: EventEmitter<Alert[]> = new EventEmitter();
    private alerts: Alert[] = [];
    private currentPage: number;
    private elements: number;
    private currentSize: number;
    private readonly i18nPrefix = 'monitoring';

    constructor(
        private readonly monitoringHttpService: MonitoringHttpService,
        private readonly utilService: UtilService
    ) {
        this.currentPage = 1;
        this.elements = 0;
        this.currentSize = 25;
    }

    getAlerts(): Alert[] {
        return this.alerts;
    }
    getCurrentPage(): number {
        return this.currentPage;
    }
    getElements(): number {
        return this.elements;
    }
    getCurrentSize(): number {
        return this.currentSize;
    }
    getSelectedJob(table: Table): Alert[] {
        return table.body.filter(row => row.selected).map(row => row.content);
    }

    loadAlerts(dgeId: number, filters: Filter[], page = this.currentPage, size = this.currentSize): void {
        this.currentPage = page;
        this.currentSize = size;

        const search = new Search(filters, [new Order(MonitoringConstants.DATE, OrderType.Desc)], [],
            new Paginator(page - 1, this.currentSize, this.elements));

        this.monitoringHttpService.getAlerts(dgeId, search, (result, totalSize) => {
            this.elements = totalSize;
            this.alerts = result as Alert[];
            this.alertsLoaded.emit(result);
        });
    }

    initTable(): Table {
        const header = this.initTableHeader();
        const body = this.initTableBody([]);

        return new Table(header, body);
    }

    initTableHeader(): TableRow {
        return new TableRow(
            [
                new TableFieldText(`${this.i18nPrefix}.table.name`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.resource.label`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.description`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.alerts`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.priority.label`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.status.label`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.date`, undefined, { style: 'text-align:center' }, undefined, undefined, true)
            ]
        );
    }

    initTableBody(alerts: Alert[]): TableRow[] {

        return alerts.map(alert => {
            const fields: TableField[] = [];
            fields.push(new TableFieldIconsText(
                new TableFieldIcons([new TableFieldIcon(['fas', 'square'], undefined, undefined, undefined, undefined, { style: `color:${alert.color} !important` })]),
                new TableFieldText(alert.name)));
            fields.push(new TableFieldText(`${this.i18nPrefix}.table.resource.option.${alert.resource}`, undefined, undefined, undefined, undefined, true));
            fields.push(new TableFieldText(alert.description, undefined, undefined));
            fields.push(this.getAlertsField(alert));
            fields.push(new TableFieldBadges([
                new TableFieldBadge(`${this.i18nPrefix}.table.priority.option.${alert.priority}`, undefined, { cssClass: this.getPriority(alert.priority) }, true)
            ]));
            fields.push(new TableFieldText(`${this.i18nPrefix}.table.status.option.${alert.status}`, undefined, undefined, undefined, undefined, true));
            fields.push(new TableFieldDate(alert.date, this.utilService.getDateFormats(), undefined, undefined, { style: 'text-align:center' }));

            const tableRow = new TableRow(fields);
            tableRow.content = alert;

            return tableRow;
        });

    }

    private getAlertsField(alert: Alert): TableFieldIcons {
        const alertsFieldData: Array<TableFieldIcon> = [];

        alert.notificationChannels.forEach(alertBy => {
            if (alertBy.via === AlertVia.Email) {
                const emailIcon = new TableFieldIcon(faEnvelope, alertBy.value, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsFieldData.push(emailIcon);
            } else if (alertBy.via === AlertVia.Sms) {
                const smsIcon = new TableFieldIcon(faComment, alertBy.value, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsFieldData.push(smsIcon);
            }
        });

        return new TableFieldIcons(alertsFieldData);
    }

    getPriority(priority: Priority): string {
        const cssClass: { [key: string]: string; } = {
            critical: 'bg-dpred',
            medium: 'bg-dpyellow',
            low: 'bg-dpgreen'
        };

        return cssClass[priority] || '';
    }

}